<script setup lang="ts">
import { useGamesStore } from '~/stores/GamesStore'

interface SectionGamesProps {
  data: SectionGameListRecord
  moduleIndex: number
}
const props = defineProps<SectionGamesProps>()

const { level, isSemantic, invertedStyles } = useSectionRules(
  props.moduleIndex,
  props.data.invertColorTheme,
  !!props.data.title
)

const route = useRoute()
const store = useGamesStore()

// Set pages per data load
store.setItemsPerPage(props.data.itemsPerPage as number)

function loadData(initial: boolean = true) {
  // Handle initial load vs. loadMore
  if (initial) {
    store.setLoading(true)
    store.resetGames()
  } else {
    store.setLoadingMore(true)
  }

  if (route.query.category) {
    // Fetch Games by Category
    const cat = store.getCategoryBySlug(route.query.category as string)

    if (cat) {
      store.pushFilter(cat)
      // Filtered by Category (DatoCMS record ID)
      store.fetchGames(cat.id)
    }
  } else if (!route.query.category && !props.data.showFilters && props.data.categories?.length) {
    // Filtered by Preselected Category (DatoCMS record ID)
    store.fetchGames(props.data?.categories[0]?.id)
  } else {
    // Unfiltered / All Games
    store.pushFilter(null)
    store.fetchGames()
  }
}

watch(
  () => route.query,
  () => {
    loadData()
  }
)

onMounted(() => {
  if (!store.allGames.length || route.query.category !== store.filteredBy?.slug) {
    loadData()
  }
})
</script>

<template>
  <div
    class="container"
    :class="[
      {
        'page-pt': data.title
      },
      invertedStyles
    ]"
  >
    <Heading
      v-if="data.title"
      class="mb-4 uppercase lg:mb-24"
      :level="level"
      :size="1"
      :is-semantic="isSemantic"
      >{{ data.title }}</Heading
    >
    <GamesPageFilter
      v-if="data.categories.length && data.showFilters"
      :categories="data.categories"
      :invert-colors="data.invertColorTheme"
    />
    <GamesList
      class="min-h-[491px]"
      :data="store.allGames"
      :is-loading="store.isLoading"
      :load-more-label="data.linkMore"
      :invert-colors="data.invertColorTheme"
    />
    <div class="min-h-[124px] py-8 pb-12 text-center">
      <Transition
        enter-from-class="opacity-0 scale-75"
        leave-to-class="opacity-0 scale-75"
        enter-active-class="transition-all duration-500"
        leave-active-class="transition-all duration-300"
      >
        <Button
          v-show="data.linkMore && store.isButtonVisible"
          :label="data.linkMore"
          :loading="store.isLoadingMore"
          class="min-w-[168px]"
          variant="outline"
          @click="loadData(false)"
        />
      </Transition>
    </div>
  </div>
</template>
